/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { CountryCode } from '@src/i18n/locales'
import { safeParse } from '@src/utils/string.util'

import { CACHE, MIDDLEWARE_TIMEOUTS, ONE_MONTH_IN_SECONDS } from './utils/constants.util'

const vercelUrl = `https://${process.env.VERCEL_URL || 'www.beachbody.com'}`
const siteMapUrl = process.env.SITEMAP_URL_OVERRIDE || vercelUrl

/**
 * PUBLIC config values that may be exposed to the client.
 */
export interface ConfigPublic {
  general: {
    env: string
    isProduction: boolean
    vercelEnv: string
    isDeployed: () => boolean
    isCacheEnabled: () => boolean
    appName: string
    appVersion: string
    customDeploymentIdEnabled: boolean
    customDeploymentId: string
    commitSHA: string
    test?: string
    beachbodyUrl: string
    bodUrl: string
    bodIosAppUrl: string
    bodAndroidAppUrl: string
    tbbUrl: string
    eligibilityRedirectUrl: string
    siteMapUrl: string
    supportedLocales: string[]
    isCartIconOnCheckout: boolean
    topNavDelay: number
    middlewareTimeouts: typeof MIDDLEWARE_TIMEOUTS
    inactivityTimeout: number
    productMaxQuantities: {
      digital: number
      nutrition: number
      fitness: number
    }
    cookiePrefix: string
    /** Locale-relative path starting with `/`; `/login` uses the standard login redirect. */
    unauthenticatedCDPRedirectPath: string
  }
  shopify: {
    env: string
    baseUrlMap: {
      [key in CountryCode]: string
    }
    accountUrlMap: {
      [key in CountryCode]: string
    }
  }
  subscriptionPurchaseUrls: {
    [key in CountryCode]: string
  }
  logs: {
    logsVisibleInConsole: boolean
    logLevelClient: string
    logLevelServer: string
    sensitiveFieldPatterns: string[]
    safeFieldPatterns: string[]
  }
  amplitude: {
    clientDeploymentKey: string
    clientDeploymentKeyDigital: string
    apiKey: string
    apiKeyDigital: string
    logLevel: number
  }
  builder: {
    apiKey: string
    cacheMap: Record<string, string>
  }
  datadog: {
    allowedTracingUrls: string[]
    applicationId: string
    clientToken: string
    logSampleRate: number
    env: string
    isEnabled: boolean
    sessionSampleRate: number
    sessionReplaySampleRate: number
    site: string
    service: string
  }
  featureFlags: {
    partnerEnabled: boolean
    guestCheckoutEnabled: boolean
    myAccountPageEnabled: boolean
    inspectUserPropertiesEnabled: boolean
    checkoutLoginEnabled: boolean
    onesiteEnabled: boolean
    onesiteP2Enabled: boolean
    faqEnabled: boolean
    shopifyEnabled: boolean
    ambassadorRailEnabled: boolean
    myPurchasesRailEnabled: boolean
    commerceRailEnabled: boolean
    isTrackEnabled: boolean
    isCocoaGamificationEnabled: boolean
    isBodiBlueEnabled: boolean
    programQuizEnabled: boolean
    unauthorizedRedirectsEnabled: boolean
  }
  myProgressModuleAdditional: string
  commerce: {
    supportedCountries: string[]
  }
  googleTagManager: {
    id: string
    auth?: string
    previewEnv?: string
  }
  googleRecaptcha: {
    siteKey: string
  }
  ketch: {
    isEnabled: boolean
    scriptUrl: string
  }
  osano: {
    isEnabled: boolean
    configId: string
    configVersion: string
  }
  consentsService: {
    apiUrl: string
    apiKey: string
  }
  threatmetrix: {
    url: string
    orgId: string
  }
  sse: {
    scriptUrl: string
    cssUrl: string
    isSplashed: boolean
    sseMigrationDate: string
    isLegacySseEnabled: boolean
  }
  sierra: {
    isEnabled: boolean
    scriptUrl: string
    pathsToCloseChat: string[]
  }
  socialLadder: {
    isEnabled: boolean
    isConversionEnabled: boolean
    isSplashed: boolean
    cookieMaxAge: string
    areaGuid: {
      BodiAffiliates: string
      BodiExperience: string
    }
    appGuid: {
      BodiAffiliates: string
      BodiExperience: string
    }
    shopCode: {
      BodiAffiliates: string
      BodiExperience: string
    }
  }
  videoPlayer: {
    scriptUrl: string
    qvThresholdPercentage: number
  }
  gladly: {
    url: string
    orgId: string
    appId: Record<string, string>
    lng: Record<string, string>
    audienceId: Record<string, string>
  }
  formsite: {
    formUrls: Record<string, string>
  }
  medallia: {
    enabled: boolean
    embedUrl: string
  }
  digioh: {
    fitnessQuizSmartTag: string
  }
  socialFeed: {
    staticBaseUrl: string
  }
  content: {
    heroBanner10MinSlug: string
    programPathEnabledSlugs: string[]
    programQuizSlug: string
  }
  gamification: {
    faqUrl: string
  }
  search: {
    suggestionsIndex: string
    programsIndex: string
    workoutsIndex: string
    recipesIndex: string
    lessonsIndex: string
    programMaterialsIndex: string
    otherVideosIndex: string
  }
}

export const configPublic: ConfigPublic = {
  general: {
    env: process.env.NEXT_PUBLIC_ENV ?? 'local',
    isProduction: process.env.NEXT_PUBLIC_VERCEL_ENV === 'production',
    vercelEnv: String(process.env.NEXT_PUBLIC_VERCEL_ENV),
    isDeployed: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    isCacheEnabled: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    appName: process.env.NEXT_PUBLIC_APP_NAME ?? 'commerce-web',
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'development',
    customDeploymentIdEnabled: process.env.NEXT_PUBLIC_CUSTOM_DEPLOYMENT_ID_ENABLED === 'true',
    customDeploymentId: process.env.NEXT_PUBLIC_CUSTOM_DEPLOYMENT_ID ?? '',
    commitSHA: (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? '').slice(0, 7),
    test: process.env.NEXT_PUBLIC_TEST,
    beachbodyUrl: process.env.NEXT_PUBLIC_BEACHBODY_URL ?? 'https://www.beachbody.com',
    bodUrl: process.env.NEXT_PUBLIC_BOD_URL ?? 'https://www.beachbodyondemand.com',
    bodIosAppUrl:
      process.env.NEXT_PUBLIC_BOD_IOS_APP_URL ??
      'https://apps.apple.com/us/app/bodi-fitness-home-workouts/id1031660123',
    bodAndroidAppUrl:
      process.env.NEXT_PUBLIC_BOD_ANDROID_APP_URL ??
      'https://play.google.com/store/apps/details?id=com.beachbody.bod',
    tbbUrl: process.env.NEXT_PUBLIC_TBB_URL ?? 'https://www.teambeachbody.com',
    eligibilityRedirectUrl: process.env.NEXT_PUBLIC_ELIGIBILITY_REDIRECT_URL ?? '/c/fitness',
    siteMapUrl,
    supportedLocales: safeParse<string[]>(process.env.NEXT_PUBLIC_SUPPORTED_LOCALES) || [
      'en-US',
      'en-CA',
      'fr-CA',
      'en-GB',
    ],
    isCartIconOnCheckout: process.env.NEXT_PUBLIC_IS_CART_ICON_ON_CHECKOUT === 'true',
    topNavDelay: safeParse<number>(process.env.NEXT_PUBLIC_TOPNAV_DELAY) ?? 200,
    middlewareTimeouts:
      safeParse<typeof MIDDLEWARE_TIMEOUTS>(process.env.NEXT_PUBLIC_MIDDLEWARE_TIMEOUTS) ||
      MIDDLEWARE_TIMEOUTS,
    inactivityTimeout: Number(safeParse<number>(process.env.NEXT_PUBLIC_INACTIVITY_TIMEOUT) || 0),
    productMaxQuantities: safeParse(process.env.NEXT_PUBLIC_PRODUCT_MAX_QUANTITIES) || {
      digital: 1,
      nutrition: 5,
      fitness: 5,
    },
    cookiePrefix: process.env.NEXT_PUBLIC_COOKIE_PREFIX ?? '',
    unauthenticatedCDPRedirectPath:
      process.env.NEXT_PUBLIC_UNAUTHENTICATED_CDP_REDIRECT_PATH ?? '/login',
  },
  shopify: {
    env: process.env.NEXT_PUBLIC_SHOPIFY_ENV ?? process.env.NEXT_PUBLIC_ENV ?? 'local',
    baseUrlMap: safeParse(process.env.NEXT_PUBLIC_SHOPIFY_BASE_URL_MAP) || {
      US: 'https://shop.bodi.com',
      CA: 'https://shop-ca.bodi.com',
      GB: 'https://shop-uk.bodi.com',
      AU: 'https://shop.bodi.com',
      FR: 'https://shop.bodi.com',
    },
    accountUrlMap: safeParse(process.env.NEXT_PUBLIC_SHOPIFY_ACCOUNT_URL_MAP) || {
      US: 'https://account.shop.bodi.com',
      CA: 'https://account.shop-ca.bodi.com',
      GB: 'https://account.shop-uk.bodi.com',
      AU: 'https://account.shop.bodi.com',
      FR: 'https://account.shop.bodi.com',
    },
  },
  subscriptionPurchaseUrls: safeParse(process.env.NEXT_PUBLIC_SUBSCRIPTION_PURCHASE_URLS) || {
    US: '/bodi-subscription',
    CA: '/bodi-subscription',
    GB: '/',
    AU: '/bodi-subscription',
    FR: '/bodi-subscription',
  },
  logs: {
    logsVisibleInConsole: process.env.NEXT_PUBLIC_LOGS_VISIBLE_IN_CONSOLE === 'true',
    logLevelClient: process.env.NEXT_PUBLIC_LOG_LEVEL_CLIENT ?? 'debug',
    logLevelServer: process.env.NEXT_PUBLIC_LOG_LEVEL_SERVER ?? 'info',
    sensitiveFieldPatterns: safeParse<string[]>(
      process.env.NEXT_PUBLIC_SENSITIVE_FIELD_PATTERNS
    ) || [
      'password',
      'token',
      'secret',
      'flexkey',
      'auth',
      'session',
      'cookie',
      'csrf',
      'jwt',
      'bearer',
      'firstName',
      'lastName',
      'address',
      'streetName',
      'additionalStreetInfo',
      'city',
      'zip',
      'postalCode',
      'email',
      'cardExpiration',
      'cvc',
      'cvv',
      'signature',
    ],
    safeFieldPatterns: safeParse<string[]>(process.env.NEXT_PUBLIC_SAFE_FIELD_PATTERNS) || [
      '^CustomerEmail$',
      '^customerEmailDomain$',
      '^clickIdCookie$',
      '^auth0ResponseHeaders$',
      '^x-auth0-requestid$',
    ],
  },
  amplitude: {
    clientDeploymentKey: process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY ?? '',
    clientDeploymentKeyDigital:
      process.env.NEXT_PUBLIC_AMPLITUDE_DIGITAL_EXPERIMENT_DEPLOYMENT_KEY ?? '',
    apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '',
    apiKeyDigital: process.env.NEXT_PUBLIC_AMPLITUDE_DIGITAL_API_KEY ?? '',
    logLevel: Number(process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL ?? 0),
  },
  builder: {
    apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY ?? '',
    cacheMap: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_BUILDER_CACHE_MAP) ?? {
      a2c65c9f3dfa480d9c395a195ea5c0f5: CACHE.page,
      '96dd32ede8cf4d098b2b95ca0be5986f': CACHE.notFoundSection,
    },
  },
  datadog: {
    allowedTracingUrls: safeParse(process.env.NEXT_PUBLIC_DATADOG_RUM_ALLOWED_TRACING_URLS) || [],
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    env: process.env.NEXT_PUBLIC_ENV ?? 'local',
    isEnabled: process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED
      ? process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED === 'true'
      : true,
    sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE ?? 100),
    logSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_LOG_SAMPLE_RATE ?? 100),
    sessionReplaySampleRate: Number(
      process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE_SESSION_REPLAY ?? 100
    ),
    site: 'datadoghq.com',
    service: 'commerce-web',
  },
  featureFlags: {
    partnerEnabled: process.env.NEXT_PUBLIC_PARTNER_ENABLED === 'true',
    guestCheckoutEnabled: process.env.NEXT_PUBLIC_GUEST_CHECKOUT_ENABLED === 'true',
    myAccountPageEnabled: process.env.NEXT_PUBLIC_MY_ACCOUNT_ENABLED === 'true',
    inspectUserPropertiesEnabled:
      process.env.NEXT_PUBLIC_INSPECT_USER_PROPERTIES_ENABLED === 'true',
    checkoutLoginEnabled: process.env.NEXT_PUBLIC_CHECKOUT_LOGIN_ENABLED === 'true',
    onesiteEnabled: process.env.NEXT_PUBLIC_ONESITE_ENABLED === 'true',
    onesiteP2Enabled: process.env.NEXT_PUBLIC_ONESITE_P2_ENABLED === 'true',
    faqEnabled: process.env.NEXT_PUBLIC_FAQ_ENABLED === 'true',
    shopifyEnabled: process.env.NEXT_PUBLIC_SHOPIFY_ENABLED === 'true',
    ambassadorRailEnabled: process.env.NEXT_PUBLIC_AMBASSADOR_RAIL_ENABLED === 'true',
    myPurchasesRailEnabled: process.env.NEXT_PUBLIC_MY_PURCHASES_RAIL_ENABLED === 'true',
    commerceRailEnabled: process.env.NEXT_PUBLIC_COMMERCE_RAIL_ENABLED === 'true',
    isTrackEnabled: process.env.NEXT_PUBLIC_IS_TRACK_ENABLED === 'true',
    isCocoaGamificationEnabled: process.env.NEXT_PUBLIC_IS_COCOA_GAMIFICATION_ENABLED === 'true',
    isBodiBlueEnabled: process.env.NEXT_PUBLIC_IS_BODI_BLUE_ENABLED === 'true',
    programQuizEnabled: process.env.NEXT_PUBLIC_PROGRAM_QUIZ_ENABLED_FLAG === 'true',
    unauthorizedRedirectsEnabled: process.env.NEXT_PUBLIC_UNAUTHORIZED_REDIRECTS_ENABLED === 'true',
  },
  myProgressModuleAdditional: process.env.NEXT_PUBLIC_MY_PROGRESS_MODULE_ADDITIONAL ?? '',
  commerce: {
    supportedCountries:
      safeParse<string[]>(process.env.NEXT_PUBLIC_COMMERCE_RAIL_SUPPORTED_COUNTRIES) ?? [],
  },
  googleTagManager: {
    id: process.env.NEXT_PUBLIC_GTM_ID ?? 'GTM-NX2LQ8F',
    auth: process.env.NEXT_PUBLIC_GTM_AUTH,
    previewEnv: process.env.NEXT_PUBLIC_GTM_PREVIEW_ENV,
  },
  googleRecaptcha: {
    siteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '',
  },
  ketch: {
    isEnabled: process.env.NEXT_PUBLIC_KETCH_ENABLED === 'true',
    scriptUrl:
      process.env.NEXT_PUBLIC_KETCH_SCRIPT_URL ??
      'https://global.ketchcdn.com/web/v3/config/bo_di/bodi_com/boot.js',
  },
  osano: {
    isEnabled: process.env.NEXT_PUBLIC_OSANO_ENABLED === 'true',
    configId: process.env.NEXT_PUBLIC_OSANO_ID ?? '',
    configVersion: process.env.NEXT_PUBLIC_OSANO_VERSION ?? 'v1.3.0',
  },
  consentsService: {
    apiUrl: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_KEY ?? '',
  },
  threatmetrix: {
    url: process.env.NEXT_PUBLIC_THREATMETRIX_URL ?? '',
    orgId: process.env.NEXT_PUBLIC_THREATMETRIX_ORG_ID ?? '',
  },
  sse: {
    scriptUrl:
      process.env.NEXT_PUBLIC_SSE_SCRIPT_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.js',
    cssUrl:
      process.env.NEXT_PUBLIC_SSE_CSS_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.css',
    isSplashed: process.env.NEXT_PUBLIC_SSE_IS_SPLASHED === 'true',
    sseMigrationDate: '2026-03-24',
    isLegacySseEnabled: process.env.NEXT_PUBLIC_SSE_IS_LEGACY_ENABLED === 'true',
  },
  sierra: {
    isEnabled: process.env.NEXT_PUBLIC_SIERRA_ENABLED === 'true',
    scriptUrl:
      process.env.NEXT_PUBLIC_SIERRA_SCRIPT_URL ??
      'https://sierra.chat/agent/Ut1QkpUBlHLzwwIBlHL4Ah5wk-5cYf0rK_kNTk3SH5E/embed',
    pathsToCloseChat: safeParse(process.env.NEXT_PUBLIC_SIERRA_PATHS_TO_CLOSE_CHAT) ?? [
      '/print-receipt',
    ],
  },
  socialLadder: {
    isEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_ENABLED === 'true',
    isConversionEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_CONVERSION_IS_ENABLED === 'true',
    isSplashed: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_SPLASHED === 'true',
    cookieMaxAge: process.env.NEXT_PUBLIC_SOCIAL_LADDER_COOKIE_MAX_AGE ?? `${ONE_MONTH_IN_SECONDS}`,
    areaGuid: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_AREA_GUID_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
    appGuid: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_APP_GUID_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
    shopCode: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_SHOP_CODE_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
  },
  videoPlayer: {
    scriptUrl:
      process.env.NEXT_PUBLIC_VIDEO_PLAYER_SCRIPT_URL ??
      'https://www.edge.player.dev.bodi.com/web-video-player.iife.js',
    qvThresholdPercentage: Number(
      process.env.NEXT_PUBLIC_VIDEO_PLAYER_QV_THRESHOLD_PERCENTAGE ?? 25
    ),
  },
  gladly: {
    url: process.env.NEXT_PUBLIC_GLADLY_URL || 'https://bodi.us-uat.gladly.qa',
    orgId: process.env.NEXT_PUBLIC_GLADLY_ORG_ID || 'G05jz7h4QDyApoeen3QDFw',
    appId: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_GLADLY_APP_ID) ?? {
      'en-US': 'bodi.com-BODi-US-English',
      'es-US': '',
      'en-CA': 'bodi.com-BODi-Canada-English',
      'fr-CA': 'bodi.com-BODi-Canada-French',
      'en-GB': '',
      'fr-FR': '',
      'en-AU': '',
    },
    lng: {
      'en-US': 'en-us',
      'es-US': 'en-us',
      'en-CA': 'en-us',
      'fr-CA': 'fr-ca',
      'en-GB': 'en-us',
      'fr-FR': 'fr-ca',
      'en-AU': 'en-us',
    },
    audienceId: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_GLADLY_AUDIENCE_ID) ?? {
      'en-US': 'XmZehuF7QhWlvHgBLOIDgQ',
      'es-US': 'XmZehuF7QhWlvHgBLOIDgQ',
      'en-CA': 'ihhQLHZEQRKsGp28-xqWhQ',
      'fr-CA': 'ihhQLHZEQRKsGp28-xqWhQ',
      'en-GB': 'q5Nrofp4SlCsQykzvqWAyQ',
      'fr-FR': 'uRC4jbd8SaeJlgSJAsqiHQ',
      'en-AU': 'x1LiibXtTUqHdYVfEw1WRg',
    },
  },
  formsite: {
    formUrls: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_FORMSITE_FORM_URLS) ?? {
      gb: 'https://fs6.formsite.com/beachbody/ahwiyqrvvb/index',
      fr: 'https://fs6.formsite.com/beachbody/4hlkgk7ddc/index',
      au: 'https://fs6.formsite.com/beachbody/tmj6aomvgr/index',
    },
  },
  medallia: {
    enabled: process.env.NEXT_PUBLIC_MEDALLIA_ENABLED === 'true',
    embedUrl:
      process.env.NEXT_PUBLIC_MEDALLIA_EMBED_URL ??
      'https://nebula-cdn.kampyle.com/us/wu/384315/onsite/embed.js',
  },
  digioh: {
    fitnessQuizSmartTag:
      process.env.NEXT_PUBLIC_DIGIOH_FITNESS_QUIZ_SMART_TAG ??
      'lightbox-inline-form-9341b860-269f-4ce1-a767-874800a9e04e',
  },
  socialFeed: {
    staticBaseUrl:
      process.env.NEXT_PUBLIC_SOCIAL_FEED_STATIC_URL ??
      'https://devices-static-qa6.beachbodyondemand.com/v2/socialFeed',
  },
  content: {
    heroBanner10MinSlug: process.env.NEXT_PUBLIC_HERO_BANNER_10MIN_SLUG ?? '',
    programPathEnabledSlugs:
      safeParse<string[]>(process.env.NEXT_PUBLIC_PROGRAM_PATH_ENABLED_SLUGS) ?? [],
    programQuizSlug: process.env.NEXT_PUBLIC_PROGRAM_QUIZ_SLUG ?? 'program-quiz-v2-all-questions',
  },
  gamification: {
    faqUrl: process.env.NEXT_PUBLIC_GAMIFICATION_FAQ_URL ?? '',
  },
  search: {
    suggestionsIndex: process.env.NEXT_PUBLIC_SEARCH_SUGGESTIONS_INDEX ?? 'AUTO_SUGGESTIONS_V2',
    programsIndex: process.env.NEXT_PUBLIC_SEARCH_PROGRAMS_INDEX ?? 'PROGRAMS_V2',
    workoutsIndex: process.env.NEXT_PUBLIC_SEARCH_WORKOUTS_INDEX ?? 'WORKOUTS_V2',
    recipesIndex: process.env.NEXT_PUBLIC_SEARCH_RECIPES_INDEX ?? 'RECIPES_V2',
    lessonsIndex: process.env.NEXT_PUBLIC_SEARCH_LESSONS_INDEX ?? 'LESSONS_V2',
    programMaterialsIndex:
      process.env.NEXT_PUBLIC_SEARCH_PROGRAM_MATERIALS_INDEX ?? 'PROGRAM_MATERIALS_V2',
    otherVideosIndex: process.env.NEXT_PUBLIC_SEARCH_OTHER_VIDEOS_INDEX ?? 'OTHER_VIDEOS_V2',
  },
}
